/**
 * Format number in compact notation (e.g., 1.2K, 3.4M)
 * @param {number|null|undefined} value - Number to format
 * @returns {string} - Formatted number string
 */
export const formatNumberCompact = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  try {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  } catch (e) {
    return Number(value).toLocaleString();
  }
};

/**
 * Format number as USD currency
 * @param {number|null|undefined} value - Number to format
 * @returns {string} - Formatted currency string (e.g., "$1,234.56")
 */
export const formatCurrencyUSD = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '$0';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
  } catch (e) {
    return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

/**
 * Format number as percentage
 * @param {number|null|undefined} value - Number to format (e.g., 0.1234 for 12.34%)
 * @param {number} [digits=1] - Number of decimal places
 * @returns {string} - Formatted percentage string (e.g., "12.3%")
 */
export const formatPercent = (value, digits = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  return `${Number(value).toFixed(digits)}%`;
};

/**
 * Safely convert value to number
 * @param {*} value - Value to convert
 * @param {number} [defaultValue=0] - Default value if conversion fails
 * @returns {number} - Converted number
 */
export const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * Safe division - prevents division by zero
 * @param {*} numerator - Numerator
 * @param {*} denominator - Denominator
 * @param {number} [defaultValue=0] - Default value if division fails
 * @returns {number} - Result of division or default value
 */
export const safeDivide = (numerator, denominator, defaultValue = 0) => {
  const num = safeNumber(numerator);
  const den = safeNumber(denominator);
  if (den === 0) return defaultValue;
  return num / den;
};

/**
 * Safely format number with toLocaleString
 * @param {*} value - Value to format
 * @param {Object} [options] - Format options
 * @returns {string} - Formatted number string
 */
export const safeFormatNumber = (value, options = {}) => {
  const num = safeNumber(value);
  try {
    return num.toLocaleString(undefined, options);
  } catch (e) {
    return String(num);
  }
};

/**
 * Safely format number with toFixed
 * @param {*} value - Value to format
 * @param {number} [digits=2] - Number of decimal places
 * @returns {string} - Formatted number string
 */
export const safeFormatFixed = (value, digits = 2) => {
  const num = safeNumber(value);
  if (isNaN(num)) return '0.' + '0'.repeat(digits);
  return num.toFixed(digits);
};

/**
 * Format number in a human-readable way for non-technical users
 * @param {number|null|undefined} value - Number to format
 * @param {Object} [options] - Formatting options
 * @returns {string} - Human-readable number string
 */
export const formatNumberReadable = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  
  const {
    compact = true,
    decimals = 1,
    showPlus = false
  } = options;

  const num = Number(value);
  
  if (compact) {
    if (Math.abs(num) >= 1000000) {
      return `${showPlus && num > 0 ? '+' : ''}${(num / 1000000).toFixed(decimals)}M`;
    }
    if (Math.abs(num) >= 1000) {
      return `${showPlus && num > 0 ? '+' : ''}${(num / 1000).toFixed(decimals)}K`;
    }
  }
  
  return `${showPlus && num > 0 ? '+' : ''}${num.toLocaleString(undefined, { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: decimals 
  })}`;
};

/**
 * Format currency in a human-readable way
 * @param {number|null|undefined} value - Amount to format
 * @param {Object} [options] - Formatting options
 * @returns {string} - Human-readable currency string
 */
export const formatCurrencyReadable = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '$0';
  
  const {
    compact = true,
    showCents = true,
    currency = 'USD'
  } = options;

  const num = Number(value);
  const symbol = currency === 'USD' ? '$' : currency;
  
  if (compact && Math.abs(num) >= 1000000) {
    return `${symbol}${(num / 1000000).toFixed(1)}M`;
  }
  if (compact && Math.abs(num) >= 1000) {
    return `${symbol}${(num / 1000).toFixed(1)}K`;
  }
  
  if (showCents) {
    return `${symbol}${num.toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  }
  
  return `${symbol}${Math.round(num).toLocaleString()}`;
};

/**
 * Format percentage in a human-readable way
 * @param {number|null|undefined} value - Percentage value (0-100)
 * @param {Object} [options] - Formatting options
 * @returns {string} - Human-readable percentage string
 */
export const formatPercentReadable = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  
  const {
    decimals = 1,
    showPlus = false,
    showSign = false
  } = options;

  const num = Number(value);
  const sign = showSign && num > 0 ? '+' : '';
  
  return `${sign}${num.toFixed(decimals)}%`;
};

